// features/workstations/dashboard-sections/personal-playbook.js
// ═══════════════════════════════════════════════════════════
// PERSONAL PLAYBOOK — Tier B learning
// ═══════════════════════════════════════════════════════════
// The pre-market re-read. Each playbook is a one-pager for one
// validated pattern, broken into the standard shape:
//   - When it fires
//   - Mechanics
//   - Entry / Exit / Sizing rules
//   - What works / What breaks
//   - Notes
//
// Re-read before the bell. “Mark reviewed” stamps a
// timestamp so future-you can see which patterns are actually
// active in your prep.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PractitionerLens,
  MetaField,
} from '../../../components/widgets/primitives';

const STATUS_OPTIONS = [
  { key: 'active',   label: 'Active'   },
  { key: 'draft',    label: 'Draft'    },
  { key: 'paused',   label: 'Paused'   },
  { key: 'retired',  label: 'Retired'  },
];

const STATUS_TONES = {
  active:  'sage',
  draft:   'gold',
  paused:  'steel',
  retired: 'steel',
};

const PATTERN_FIELD_LABELS = {
  when_it_fires:  'when it fires',
  mechanics:      'mechanics',
  entry_rules:    'entry rules',
  exit_rules:     'exit rules',
  sizing_rules:   'sizing rules',
  what_works:     'what works',
  what_breaks:    'what breaks',
  notes:          'notes',
};
const PATTERN_FIELD_ORDER = [
  'when_it_fires', 'mechanics', 'entry_rules', 'exit_rules',
  'sizing_rules', 'what_works', 'what_breaks', 'notes',
];

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function PersonalPlaybookSection({ workstation, userId }) {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [composing, setComposing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('active');

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        workstation_id: workstation.id,
        user_id: userId,
      };
      if (filterStatus !== 'all') params.status = filterStatus;
      const qs = new URLSearchParams(params);
      const res = await fetch(`/api/workstations/playbooks?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setPlaybooks(Array.isArray(payload?.playbooks) ? payload.playbooks : []);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async (payload) => {
    setSaving(true); setError(null);
    try {
      const res = await fetch('/api/workstations/playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstation.id,
          user_id: userId,
          ...payload,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return false;
      }
      setComposing(false);
      await load();
      return true;
    } catch (e) {
      setError(e?.message || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [workstation?.id, userId, load]);

  const update = useCallback(async (id, patch) => {
    setSaving(true); setError(null);
    try {
      const res = await fetch('/api/workstations/playbooks', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, user_id: userId, patch }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return false;
      }
      setEditingId(null);
      await load();
      return true;
    } catch (e) {
      setError(e?.message || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [userId, load]);

  const markReviewed = useCallback(async (id) => {
    setSaving(true);
    try {
      await fetch('/api/workstations/playbooks?action=mark_reviewed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, user_id: userId }),
      });
      await load();
    } finally {
      setSaving(false);
    }
  }, [userId, load]);

  const transition = useCallback(async (id, status) => {
    setSaving(true);
    try {
      await fetch('/api/workstations/playbooks?action=transition', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, user_id: userId, status }),
      });
      await load();
    } finally {
      setSaving(false);
    }
  }, [userId, load]);

  const remove = useCallback(async (id) => {
    if (!confirm('Delete this playbook? This cannot be undone.')) return;
    setSaving(true);
    try {
      const qs = new URLSearchParams({ id, user_id: userId });
      await fetch(`/api/workstations/playbooks?${qs}`, { method: 'DELETE' });
      await load();
    } finally {
      setSaving(false);
    }
  }, [userId, load]);

  return (
    <section className="ws-playbook">
      {loading && <div className="ws-playbook-loading">Loading…</div>}

      {!loading && (
        <PlaybookBody
          playbooks={playbooks}
          saving={saving}
          error={error}
          composing={composing}
          setComposing={setComposing}
          editingId={editingId}
          setEditingId={setEditingId}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          onCreate={create}
          onUpdate={update}
          onMarkReviewed={markReviewed}
          onTransition={transition}
          onDelete={remove}
        />
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// BODY
// ═══════════════════════════════════════════════════════════
function PlaybookBody({
  playbooks, saving, error,
  composing, setComposing,
  editingId, setEditingId,
  filterStatus, setFilterStatus,
  onCreate, onUpdate, onMarkReviewed, onTransition, onDelete,
}) {
  const total = playbooks.length;
  const activeCount = playbooks.filter(p => p.status === 'active').length;
  const staleCount = playbooks.filter(p => {
    if (p.status !== 'active') return false;
    if (!p.last_reviewed_at) return true;
    const days = (Date.now() - new Date(p.last_reviewed_at).getTime()) / 86400000;
    return days > 60;
  }).length;

  const headline = total === 0
    ? 'No playbooks yet.'
    : staleCount > 0
      ? `${activeCount} active · ${staleCount} not re-read in 60+ days.`
      : `${activeCount} active${activeCount > 0 ? ' · all re-read recently' : ''}.`;
  const headlineTone = total === 0 ? 'steel' : staleCount > 0 ? 'amber' : 'sage';

  const summary = total === 0
    ? 'Write up your validated patterns one at a time. The pre-market re-read — “which one is firing today?” — only works if the playbooks exist.'
    : staleCount > 0
      ? 'Stale playbooks drift. Open the ones flagged below, edit anything that’s changed, mark reviewed.'
      : 'Active playbooks are current. Re-read the relevant one before the bell.';

  return (
    <>
      <Eyebrow parts={[
        'personal playbook',
        total > 0 ? `${total} total` : 'empty',
        staleCount > 0 ? `${staleCount} stale` : null,
      ]} />

      <SerifHeadline>
        <span className={`ws-playbook-tone-${headlineTone}`}>{headline}</span>
      </SerifHeadline>
      <SerifSummary>{summary}</SerifSummary>

      {error && <div className="ws-playbook-error">{error}</div>}

      <div className="ws-playbook-actions">
        <button
          type="button"
          className="ws-playbook-btn ws-playbook-btn-sage"
          onClick={() => setComposing(!composing)}
          disabled={saving}
        >
          {composing ? '✕ cancel' : '+ new playbook'}
        </button>

        <div className="ws-playbook-filter-row">
          <span className="ws-playbook-filter-label">show</span>
          <button
            type="button"
            className={`ws-playbook-filter-chip ${filterStatus === 'all' ? 'is-active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            all
          </button>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`ws-playbook-filter-chip ${filterStatus === s.key ? 'is-active' : ''}`}
              onClick={() => setFilterStatus(s.key)}
            >
              {s.label.toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {composing && (
        <PlaybookForm
          mode="create"
          saving={saving}
          onSubmit={onCreate}
          onCancel={() => setComposing(false)}
        />
      )}

      {playbooks.length > 0 && (
        <SectionStrip
          label={`playbooks${filterStatus !== 'all' ? ` · ${filterStatus}` : ''}`}
          count={playbooks.length}
        >
          <div className="ws-playbook-list">
            {playbooks.map((p) => (
              <PlaybookCard
                key={p.id}
                playbook={p}
                saving={saving}
                isEditing={editingId === p.id}
                onEdit={() => setEditingId(p.id)}
                onCancelEdit={() => setEditingId(null)}
                onSaveEdit={(patch) => onUpdate(p.id, patch)}
                onMarkReviewed={() => onMarkReviewed(p.id)}
                onTransition={(status) => onTransition(p.id, status)}
                onDelete={() => onDelete(p.id)}
              />
            ))}
          </div>
        </SectionStrip>
      )}

      <PractitionerLens label="how this earns its slot">
        Playbooks decay. A pattern you wrote up six months ago and never
        re-read isn’t part of your active arsenal — it’s a
        souvenir. The 60-day re-read flag surfaces that drift. If a
        playbook hasn’t earned a re-read in two months, retire it
        or refresh it.
      </PractitionerLens>
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// PLAYBOOK CARD
// ═══════════════════════════════════════════════════════════
function PlaybookCard({
  playbook, saving, isEditing,
  onEdit, onCancelEdit, onSaveEdit,
  onMarkReviewed, onTransition, onDelete,
}) {
  const [expanded, setExpanded] = useState(false);
  const tone = STATUS_TONES[playbook.status] || 'steel';
  const statusLabel = STATUS_OPTIONS.find(s => s.key === playbook.status)?.label || playbook.status;
  const reviewed = fmtReviewedAge(playbook.last_reviewed_at);

  if (isEditing) {
    return (
      <div className={`ws-playbook-card ws-playbook-card-${tone}`}>
        <PlaybookForm
          mode="edit"
          initial={playbook}
          saving={saving}
          onSubmit={(patch) => onSaveEdit(patch)}
          onCancel={onCancelEdit}
        />
      </div>
    );
  }

  return (
    <div className={`ws-playbook-card ws-playbook-card-${tone}`}>
      <button
        type="button"
        className="ws-playbook-card-head"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="ws-playbook-card-row">
          <span className="ws-playbook-name">{playbook.name}</span>
          <span className={`ws-playbook-status ws-playbook-status-${tone}`}>{statusLabel.toLowerCase()}</span>
          <span className={`ws-playbook-reviewed ws-playbook-reviewed-${reviewed.tone}`}>
            {reviewed.text}
          </span>
        </div>
        {playbook.when_it_fires && (
          <div className="ws-playbook-when-preview">{truncate(playbook.when_it_fires, 140)}</div>
        )}
      </button>

      {expanded && (
        <div className="ws-playbook-drawer">
          {PATTERN_FIELD_ORDER.map((f) => (
            playbook[f] && (
              <MetaField key={f} label={PATTERN_FIELD_LABELS[f]}>
                <span className="ws-playbook-prose">{playbook[f]}</span>
              </MetaField>
            )
          ))}

          <div className="ws-playbook-drawer-actions">
            <button
              type="button"
              className="ws-playbook-btn ws-playbook-btn-sage"
              onClick={onMarkReviewed}
              disabled={saving}
              title="Stamps last_reviewed_at to now"
            >
              ✓ mark reviewed
            </button>
            <button
              type="button"
              className="ws-playbook-btn ws-playbook-btn-steel"
              onClick={onEdit}
              disabled={saving}
            >
              edit
            </button>

            {/* Transition controls */}
            {playbook.status !== 'active' && (
              <button
                type="button"
                className="ws-playbook-btn ws-playbook-btn-sage"
                onClick={() => onTransition('active')}
                disabled={saving}
              >
                activate
              </button>
            )}
            {playbook.status === 'active' && (
              <button
                type="button"
                className="ws-playbook-btn ws-playbook-btn-steel"
                onClick={() => onTransition('paused')}
                disabled={saving}
              >
                pause
              </button>
            )}
            {playbook.status !== 'retired' && (
              <button
                type="button"
                className="ws-playbook-btn ws-playbook-btn-steel"
                onClick={() => onTransition('retired')}
                disabled={saving}
              >
                retire
              </button>
            )}

            <button
              type="button"
              className="ws-playbook-btn ws-playbook-btn-terra"
              onClick={onDelete}
              disabled={saving}
            >
              delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// COMPOSE / EDIT FORM
// ═══════════════════════════════════════════════════════════
function PlaybookForm({ mode, initial, saving, onSubmit, onCancel }) {
  const [name, setName] = useState(initial?.name || '');
  const [status, setStatus] = useState(initial?.status || 'draft');
  const [fields, setFields] = useState(() => {
    const init = {};
    for (const f of PATTERN_FIELD_ORDER) init[f] = initial?.[f] || '';
    return init;
  });

  const canSubmit = name.trim().length >= 2 && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const payload = mode === 'edit'
      ? { name: name.trim(), status, ...fields }
      : { name: name.trim(), status, ...fields };
    if (mode === 'edit') {
      // PATCH expects { id, patch: {...} } — caller wraps it
      await onSubmit(payload);
    } else {
      await onSubmit(payload);
    }
  };

  return (
    <form className="ws-playbook-form" onSubmit={submit}>
      <div className="ws-playbook-form-row">
        <label className="ws-playbook-field-inline">
          <span className="ws-playbook-field-label">name</span>
          <input
            type="text"
            className="ws-playbook-input ws-playbook-input-name"
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Yellow Candle v3"
            autoFocus
          />
        </label>
        <label className="ws-playbook-field-inline">
          <span className="ws-playbook-field-label">status</span>
          <select
            className="ws-playbook-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </label>
      </div>

      {PATTERN_FIELD_ORDER.map((f) => (
        <label className="ws-playbook-field" key={f}>
          <span className="ws-playbook-field-label">{PATTERN_FIELD_LABELS[f]}</span>
          <textarea
            className="ws-playbook-textarea"
            rows={f === 'mechanics' || f === 'notes' ? 3 : 2}
            value={fields[f]}
            onChange={(e) => setFields({ ...fields, [f]: e.target.value })}
            placeholder={PLACEHOLDER_BY_FIELD[f] || ''}
          />
        </label>
      ))}

      <div className="ws-playbook-actions">
        <button type="submit" disabled={!canSubmit} className="ws-playbook-btn ws-playbook-btn-sage">
          {saving ? 'saving…' : (mode === 'edit' ? 'save' : 'create')}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className="ws-playbook-btn ws-playbook-btn-steel">
          cancel
        </button>
      </div>
    </form>
  );
}

const PLACEHOLDER_BY_FIELD = {
  when_it_fires: 'Conditions that trigger this. Specific. “Gap-down day, VIX <18, breadth >60%.”',
  mechanics: 'How to read the setup. Chart structure, indicators, time-of-day.',
  entry_rules: 'Exact entry trigger. Limit / stop / market. Confirmation candle?',
  exit_rules: 'Stop placement and target rules. When to flatten early.',
  sizing_rules: 'Position size for this setup. R-based or % of account.',
  what_works: 'When this pattern produces — the conditions that boost edge.',
  what_breaks: 'When this pattern fails. Conditions that invalidate.',
  notes: 'Anything else — examples, key mistakes, links to logs.',
};

// ─── Helpers ───
function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

function fmtReviewedAge(iso) {
  if (!iso) return { text: 'never reviewed', tone: 'amber' };
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return { text: 'reviewed today', tone: 'sage' };
  if (days === 1) return { text: 'reviewed yesterday', tone: 'sage' };
  if (days < 7) return { text: `reviewed ${days}d ago`, tone: 'sage' };
  if (days < 30) return { text: `reviewed ${days}d ago`, tone: 'steel' };
  if (days < 60) return { text: `reviewed ${days}d ago`, tone: 'amber' };
  return { text: `reviewed ${days}d ago — stale`, tone: 'terra' };
}
